import type { DeskImprovement, DeskReport, GoalStatus, ImprovementVerdict, WorkItemDetail } from '@deskworks/shared-types';
import { CheckCheck, ClipboardList, Crown, Sparkles } from 'lucide-react';
import { Section } from '@/components/section';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const GOAL_LOOK: Record<GoalStatus, { dot: string; label: string }> = {
  on_track: { dot: 'bg-emerald-500', label: t('on track') },
  at_risk: { dot: 'bg-amber-500', label: t('at risk') },
  off_track: { dot: 'bg-red-500', label: t('off track') },
  no_data: { dot: 'bg-zinc-300 dark:bg-zinc-600', label: t('no data') },
};

/** Where the round stands, as people read it (docs/features/desk-reports.md). */
export function reportState(r: DeskReport): { label: string; tone: 'blue' | 'accent' | 'amber' | 'green' | 'muted' } {
  if (r.phase === 'reporting') return { label: t('Being written'), tone: 'muted' };
  if (r.phase === 'feedback') return r.answered ? { label: t('In discussion'), tone: 'accent' } : { label: t('New'), tone: 'blue' };
  if (r.phase === 'improving') return { label: t('Improving'), tone: 'amber' };
  return r.improve ? { label: t('Improved'), tone: 'green' } : { label: t('Closed'), tone: 'muted' };
}

export function ReportStateBadge({ report }: { report: DeskReport }) {
  const state = reportState(report);
  return <Badge tone={state.tone}>{state.label}</Badge>;
}

/** One dot per goal; with `notes`, a line per goal. */
export function GoalBadges({ report, notes }: { report: DeskReport; notes?: boolean }) {
  if (!report.goals.length) return null;
  if (!notes) {
    return (
      <span className="inline-flex items-center gap-1">
        {report.goals.map((g) => (
          <span key={g.title} title={`${g.title}: ${GOAL_LOOK[g.status].label}${g.note ? ` – ${g.note}` : ''}`} className={cn('size-2.5 rounded-full', GOAL_LOOK[g.status].dot)} />
        ))}
      </span>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {report.goals.map((g) => (
        <li key={g.title} className="flex items-start gap-2 text-sm">
          <span className={cn('mt-1.5 size-2.5 shrink-0 rounded-full', GOAL_LOOK[g.status].dot)} />
          <span className="min-w-0">
            <span className="font-medium">{g.title}</span>
            <span className="text-muted-foreground"> · {GOAL_LOOK[g.status].label}</span>
            {g.note && <span className="block text-xs text-muted-foreground">{g.note}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function reportPeriod(r: DeskReport) {
  const day = (iso: string) => new Date(iso).toLocaleDateString(intlLocale, { day: 'numeric', month: 'short' });
  return `${day(r.periodStart)} – ${day(r.periodEnd)}`;
}

/** On a report's Work Item page: the round, its goals and "Done – improve". */
export function ReportCard({ item, report }: { item: WorkItemDetail; report: DeskReport }) {
  const lead = report.leadName ?? t('The Lead');
  const close = useApiMutation(() => api.reports.close(item.id), {
    invalidate: [qk.workItem(item.id), qk.reportsAll(item.workspaceId)],
    success: report.improve ? t('Discussion closed — {lead} improves the desk', { lead }) : t('Discussion closed'),
  });
  const text =
    report.phase === 'reporting'
      ? t('{lead} writes the report.', { lead })
      : report.phase === 'feedback'
        ? report.improve
          ? t(
              'Answer {lead} in the thread below: questions, corrections, what should change. {lead} answers each message. When everything is said, close the discussion and {lead} improves the desk’s playbooks and skills, aimed at its goals.',
              { lead },
            )
          : t('Answer {lead} in the thread below: questions, corrections, what should change. {lead} answers each message. When everything is said, close the discussion.', { lead })
        : report.phase === 'improving'
          ? t('The discussion is closed. {lead} now improves the desk’s playbooks and skills.', { lead })
          : report.improve
            ? t('{lead} improved the desk after the discussion; the next report checks whether it helped.', { lead })
            : t('The discussion is closed.');
  return (
    <Section
      icon={<ClipboardList />}
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('Report')} <ReportStateBadge report={report} />
          {report.quiet && <Badge tone="muted">{t('quiet period')}</Badge>}
        </span>
      }
      description={
        <span title={`${formatAbsolute(report.periodStart)} – ${formatAbsolute(report.periodEnd)}`}>
          {reportPeriod(report)}
          {report.summary ? ` · ${report.summary}` : ''}
        </span>
      }
      actions={
        report.phase === 'feedback' ? (
          <Button size="sm" onClick={() => close.mutate()} loading={close.isPending}>
            {report.improve ? <Sparkles /> : <CheckCheck />} {report.improve ? t('Done – improve') : t('Close discussion')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <GoalBadges report={report} notes />
        {!!report.judged?.length && <Improvements title={t('What the last changes did')} items={report.judged} />}
        {!!report.improvements?.length && <Improvements title={t('What {lead} changed after this report', { lead })} items={report.improvements} />}
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Crown className="mt-0.5 size-4 shrink-0 text-amber-500" />
          {text}
        </p>
      </div>
    </Section>
  );
}

const VERDICT: Record<ImprovementVerdict, { label: string; tone: 'muted' | 'green' | 'amber' | 'red' }> = {
  pending: { label: t('next report checks it'), tone: 'muted' },
  helped: { label: t('helped'), tone: 'green' },
  no_effect: { label: t('no effect'), tone: 'amber' },
  worse: { label: t('worse'), tone: 'red' },
};

/** Changes the Lead made to improve its desk, with what they are for and the verdict of the next report. */
function Improvements({ title, items }: { title: string; items: DeskImprovement[] }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{title}</p>
      <ul className="flex flex-col gap-2">
        {items.map((i) => (
          <li key={i.id} className="rounded-lg border px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {i.subject === 'playbook' ? t('Playbook “{name}” v{version}', { name: i.name, version: i.toVersion }) : t('Skill “{name}” v{version}', { name: i.name, version: i.toVersion })}
              </span>
              <span className="text-xs text-muted-foreground">{t('for “{goal}”', { goal: i.goalTitle })}</span>
              {i.source === 'feedback' && <Badge tone="blue">{t('your feedback')}</Badge>}
              <Badge tone={VERDICT[i.verdict].tone}>{VERDICT[i.verdict].label}</Badge>
              {i.revertedAt && <Badge tone="muted">{t('reverted')}</Badge>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {i.reason} <span className="text-foreground/80">{t('Expected: {expectation}', { expectation: i.expectation })}</span>
              {i.metricName ? ` (${i.metricName})` : ''}
            </p>
            {i.verdictNote && <p className="mt-1 text-xs">{i.verdictNote}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
